import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type {
  CreatePurchaseOrderRequest,
  CreateSupplierRequest,
  PurchaseOrderDto,
  PurchaseOrderStatus,
  ReceivePurchaseOrderRequest,
  SupplierDto,
  UpdateSupplierRequest,
} from './types';

const QK = {
  suppliers: ['purchasing', 'suppliers'] as const,
  purchaseOrders: (branchId: string | null, status: PurchaseOrderStatus | null) =>
    ['purchasing', 'purchase-orders', branchId, status] as const,
};

// ---- Suppliers ----

export function useSuppliers() {
  return useQuery({
    queryKey: QK.suppliers,
    queryFn: async () => (await apiClient.get<SupplierDto[]>('/api/suppliers')).data,
    staleTime: 30_000,
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: CreateSupplierRequest) =>
      (await apiClient.post<SupplierDto>('/api/suppliers', req)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.suppliers }),
  });
}

export function useUpdateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, req }: { id: string; req: UpdateSupplierRequest }) =>
      (await apiClient.put<SupplierDto>(`/api/suppliers/${id}`, req)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.suppliers }),
  });
}

// ---- Purchase orders ----

export function usePurchaseOrders(
  branchId: string | null,
  status: PurchaseOrderStatus | null,
) {
  return useQuery({
    queryKey: QK.purchaseOrders(branchId, status),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (branchId) params['branchId'] = branchId;
      if (status) params['status'] = String(status);
      return (await apiClient.get<PurchaseOrderDto[]>('/api/purchase-orders', { params })).data;
    },
    staleTime: 10_000,
  });
}

export function useCreatePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: CreatePurchaseOrderRequest) =>
      (await apiClient.post<PurchaseOrderDto>('/api/purchase-orders', req)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['purchasing', 'purchase-orders'] }),
  });
}

export function useReceivePurchaseOrder(poId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: ReceivePurchaseOrderRequest) =>
      (await apiClient.post<PurchaseOrderDto>(`/api/purchase-orders/${poId}/receive`, req)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchasing', 'purchase-orders'] });
      // Receipts move stock — invalidate inventory queries too.
      qc.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}
